import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import { unstopped, type Account, type Role, type Transfer } from '@/data/types'
import { STOP_COPY } from '@/lib/status'

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

export interface AnalyticsTransferRow {
  eventId: string
  occurredAt: string
  targetId: string
  targetName?: string
  targetRole?: string
  parentId?: string | null
  parentName?: string | null
  direction: 'give' | 'take'
  amountMinor: MoneyMinor
  signedAmountMinor?: MoneyMinor
  runningTransferredMinor?: MoneyMinor
  /** Live wallet balance for the target NOW — not post-movement historical. */
  targetBalanceMinor?: MoneyMinor
}

export interface AnalyticsTransfersPage {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  rows: AnalyticsTransferRow[]
}

export type TransferRowView = Transfer & { from: Account | null; to: Account | null }

export type TransferMappedResult = {
  rows: TransferRowView[]
  total: number
  asOf?: string
  martStale?: boolean
  /** Live analytics only returns target's wallet balance now — not both post-movement ledgers. */
  balanceAfterIsLive: true
  totals: { given: number; taken: number; net: number; count: number }
  daily: { date: string; given: number; taken: number }[]
}

const KNOWN_ROLES = new Set<Role>([
  'company-master',
  'triple-master',
  'double-master',
  'super-master',
  'master',
  'player',
])

function toRole(raw?: string): Role {
  if (!raw) return 'player'
  const kebab = raw.trim().toLowerCase().replace(/[\s_]+/g, '-')
  if (KNOWN_ROLES.has(kebab as Role)) return kebab as Role
  return 'player'
}

function stubAccount(
  id: string,
  name: string,
  role?: string,
  parentId?: string | null,
): Account {
  return {
    id,
    username: name || id.slice(0, 8),
    displayName: name || id.slice(0, 8),
    role: toRole(role),
    parentId: parentId ?? null,
    path: parentId ? [parentId] : [],
    status: 'active',
    createdAt: '',
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
      premiumSportsbook: true,
    },
    childCount: 0,
    memberCount: 0,
    flags: [],
    // An audit/reporting stub, not a hierarchy row: nothing here can be stopped,
    // and no backend behind it could enforce a control if one were offered.
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}

/**
 * Map one OpenAPI TransferRow → console Transfer + stub accounts.
 * hierarchy only permits parent↔direct-child: give = parent→target, take = target→parent.
 */
export function toTransferRowView(row: AnalyticsTransferRow): TransferRowView {
  const parentId = row.parentId ?? ''
  const targetId = row.targetId
  const amount = fromMinorUnits(row.amountMinor)
  const targetLive = fromMinorUnitsSigned(row.targetBalanceMinor)
  const parentName = row.parentName?.trim() || parentId.slice(0, 8) || 'parent'
  const targetName = row.targetName?.trim() || targetId.slice(0, 8)

  const target = stubAccount(targetId, targetName, row.targetRole, parentId || null)
  const parent = parentId
    ? stubAccount(parentId, parentName, undefined, null)
    : null

  if (row.direction === 'give') {
    return {
      id: row.eventId,
      ref: row.eventId,
      at: row.occurredAt,
      fromId: parentId,
      toId: targetId,
      amount,
      direction: 'give',
      remark: '',
      // Historical post-movement balances are not in the mart — live target wallet only.
      balanceAfterFrom: 0,
      balanceAfterTo: targetLive,
      byId: parentId,
      from: parent,
      to: target,
    }
  }

  return {
    id: row.eventId,
    ref: row.eventId,
    at: row.occurredAt,
    fromId: targetId,
    toId: parentId,
    amount,
    direction: 'take',
    remark: '',
    balanceAfterFrom: targetLive,
    balanceAfterTo: 0,
    byId: parentId,
    from: target,
    to: parent,
  }
}

function isoDay(at: string): string {
  return at.slice(0, 10)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Aggregate OpenAPI pages into a TransferMappedResult for `rootId`
 * (totals/daily are relative to that node, matching fixture listTransfers).
 */
export function toTransferResult(
  pages: AnalyticsTransfersPage[],
  rootId: string,
): TransferMappedResult {
  const first = pages[0]
  const rows = pages
    .flatMap((p) => p.rows ?? [])
    .map(toTransferRowView)
    .sort((a, b) => b.at.localeCompare(a.at))

  const totals = rows.reduce(
    (acc, t) => {
      if (t.fromId === rootId) acc.given += t.amount
      else if (t.toId === rootId) acc.taken += t.amount
      acc.count += 1
      return acc
    },
    { given: 0, taken: 0, net: 0, count: 0 },
  )
  totals.given = round2(totals.given)
  totals.taken = round2(totals.taken)
  totals.net = round2(totals.taken - totals.given)

  const dailyMap = new Map<string, { date: string; given: number; taken: number }>()
  for (const t of rows) {
    const date = isoDay(t.at)
    const entry = dailyMap.get(date) ?? { date, given: 0, taken: 0 }
    if (t.fromId === rootId) entry.given += t.amount
    else if (t.toId === rootId) entry.taken += t.amount
    dailyMap.set(date, entry)
  }

  return {
    rows,
    total: rows.length,
    asOf: first?.asOf,
    martStale: first?.martStale,
    balanceAfterIsLive: true,
    totals,
    daily: [...dailyMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
  }
}
